import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, FileText, MapPin } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, API } from "@/lib/api";

const empty = {
  reporter_name: "", owner_name: "", owner_phone: "", owner_email: "",
  comune: "", zona: "", property_type: "", owner_aware: false, notes: "",
  latitude: null, longitude: null, formatted_address: "",
};

export default function NewReferral() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [consents, setConsents] = useState({ privacy: false, program_terms: false, data_legitimacy: false, marketing: false });
  const [loading, setLoading] = useState(false);
  const [dupWarn, setDupWarn] = useState(false);
  const [done, setDone] = useState(null);
  const [contractOk, setContractOk] = useState(null); // null = loading
  const [addrQuery, setAddrQuery] = useState("");
  const [addrSugg, setAddrSugg] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const addrTimer = useRef(null);

  useEffect(() => {
    api.get("/meta").then((r) => setTypes(r.data.property_types)).catch(() => {});
    api.get("/me/contract-status").then((r) => setContractOk(r.data.accepted)).catch((e) => setContractOk(e.response ? false : true));
    if (user) setForm((f) => ({ ...f, reporter_name: user.name || user.email || "" }));
  }, [user]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const searchAddress = (v) => {
    setAddrQuery(v);
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (!v || v.trim().length < 3) { setAddrSugg([]); return; }
    addrTimer.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const { data } = await api.get(`/geocode/search`, { params: { q: v } });
        setAddrSugg(data);
      } catch { setAddrSugg([]); }
      finally { setAddrLoading(false); }
    }, 450);
  };

  const pickAddress = (s) => {
    setForm((f) => ({
      ...f,
      formatted_address: s.display_name,
      latitude: s.lat,
      longitude: s.lon,
      comune: f.comune || s.comune || "",
    }));
    setAddrQuery(s.display_name);
    setAddrSugg([]);
  };

  const clearAddress = () => {
    setForm((f) => ({ ...f, formatted_address: "", latitude: null, longitude: null }));
    setAddrQuery("");
    setAddrSugg([]);
  };

  const steps = [
    { valid: () => form.owner_name && form.owner_phone },
    { valid: () => form.comune && form.property_type },
    { valid: () => true },
    { valid: () => consents.privacy && consents.program_terms && consents.data_legitimacy },
  ];

  const next = () => {
    if (!steps[step].valid()) { toast.error("Completa i campi obbligatori."); return; }
    setStep(step + 1);
  };

  const submit = async (force = false) => {
    setLoading(true);
    try {
      const payload = { ...form, consents: { ...consents, terms_version: "v1.0" }, force };
      const { data } = await api.post("/referrals", payload);
      if (data.status === "duplicate") { setDupWarn(true); setLoading(false); return; }
      setDone(data.referral);
      toast.success("Segnalazione inviata!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <DashboardLayout title="Segnalazione inviata">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center rounded-2xl border border-[#D4AF37]/40 bg-white p-10 mt-6">
          <CheckCircle2 className="h-14 w-14 text-[#D4AF37] mx-auto" />
          <h2 className="mt-4 font-display text-2xl font-semibold text-slate-900">Grazie, ci pensiamo noi!</h2>
          <p className="mt-2 text-slate-500">Abbiamo ricevuto la tua segnalazione. Il codice della pratica è:</p>
          <p className="mt-3 font-mono text-2xl font-bold text-[#8a6d0f]" data-testid="referral-code">{done.code}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate(`/segnalazioni/${done.id}`)} className="btn-gold rounded-full px-6 py-3 font-semibold">Segui la pratica</button>
            <button onClick={() => navigate("/dashboard")} className="rounded-full border border-slate-300 px-6 py-3 font-medium text-slate-700 hover:bg-slate-50">Vai alla dashboard</button>
          </div>
        </motion.div>
      </DashboardLayout>
    );
  }

  const labels = ["Il proprietario", "L'immobile", "Dettagli", "Consensi"];

  if (contractOk === false) {
    return (
      <DashboardLayout title="Segnala un immobile">
        <div className="max-w-lg mx-auto text-center rounded-2xl border border-[#D4AF37]/40 bg-white p-10 mt-6" data-testid="contract-gate">
          <FileText className="h-14 w-14 text-[#D4AF37] mx-auto" />
          <h2 className="mt-4 font-display text-2xl font-semibold text-slate-900">Firma prima il contratto</h2>
          <p className="mt-2 text-slate-500">
            Per inviare una segnalazione devi leggere e accettare i Termini e Condizioni del Programma Segnalatori.
            È un passaggio una tantum.
          </p>
          <button onClick={() => navigate("/contratto")} data-testid="go-to-contract-btn"
            className="btn-gold rounded-full px-6 py-3 font-semibold mt-6 inline-flex items-center gap-2">
            Vai al contratto <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Segnala un immobile">
      <div className="max-w-2xl mx-auto">
        {/* progress */}
        <div className="flex items-center gap-2 mb-8">
          {labels.map((l, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#D4AF37]" : "bg-slate-200"}`} />
              <p className={`mt-2 text-xs ${i === step ? "text-slate-900 font-medium" : "text-slate-400"}`}>{l}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              {step === 0 && (
                <div className="space-y-4">
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-600" data-testid="reporter-info">
                    Stai segnalando come <strong className="text-slate-900">{user?.name}</strong>. Qui inserisci i dati del <strong>proprietario</strong> che potrebbe voler vendere.
                  </div>
                  <Field label="Nome del proprietario (chi vuole vendere)" required value={form.owner_name} onChange={(v) => set("owner_name", v)} testid="f-owner-name" />
                  <Field label="Telefono del proprietario" required value={form.owner_phone} onChange={(v) => set("owner_phone", v)} testid="f-owner-phone" />
                  <Field label="Email del proprietario (se disponibile)" type="email" value={form.owner_email} onChange={(v) => set("owner_email", v)} testid="f-owner-email" />
                </div>
              )}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="relative">
                    <label className="text-sm font-medium text-slate-700">Cerca l'indirizzo dell'immobile</label>
                    <p className="text-xs text-slate-400 mb-1.5">Digita via e civico, poi scegli dai suggerimenti per salvare la posizione esatta.</p>
                    <input data-testid="f-address-search" value={addrQuery}
                      onChange={(e) => searchAddress(e.target.value)}
                      placeholder="Es. Via Roma 12, Monreale"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" autoComplete="off" />
                    {addrLoading && <p className="mt-1 text-xs text-slate-400">Ricerca in corso…</p>}
                    {addrSugg.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="address-suggestions">
                        {addrSugg.map((s, i) => (
                          <button key={i} type="button" onClick={() => pickAddress(s)} data-testid={`address-option-${i}`}
                            className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                            <MapPin className="h-4 w-4 text-[#8a6d0f] mt-0.5 shrink-0" /> <span>{s.display_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {form.latitude != null && form.longitude != null && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="selected-location">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs text-emerald-800 flex items-start gap-1.5"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {form.formatted_address}</p>
                        <button type="button" onClick={clearAddress} data-testid="clear-location-btn" className="text-xs font-medium text-slate-500 hover:text-slate-700 shrink-0">Cambia</button>
                      </div>
                      <iframe
                        title="Anteprima posizione"
                        className="mt-2 w-full h-40 rounded-md border border-emerald-200"
                        loading="lazy"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${form.longitude - 0.004}%2C${form.latitude - 0.003}%2C${form.longitude + 0.004}%2C${form.latitude + 0.003}&layer=mapnik&marker=${form.latitude}%2C${form.longitude}`}
                      />
                    </div>
                  )}

                  <Field label="Comune dell'immobile" required value={form.comune} onChange={(v) => set("comune", v)} testid="f-comune" />
                  <Field label="Zona / indirizzo (se conosciuto)" value={form.zona} onChange={(v) => set("zona", v)} testid="f-zona" />
                  <div>
                    <label className="text-sm font-medium text-slate-700">Tipologia immobile *</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {types.map((t) => (
                        <button key={t} type="button" onClick={() => set("property_type", t)} data-testid={`type-${t}`}
                          className={`rounded-lg border px-4 py-3 text-sm font-medium text-left transition-colors ${
                            form.property_type === t ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#8a6d0f]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}>{t}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Il proprietario sa che verrà contattato?</label>
                    <div className="mt-2 flex gap-3">
                      {[["Sì", true], ["No", false]].map(([l, v]) => (
                        <button key={l} type="button" onClick={() => set("owner_aware", v)} data-testid={`aware-${l}`}
                          className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                            form.owner_aware === v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#8a6d0f]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Raccontaci brevemente quello che sai</label>
                    <textarea data-testid="f-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Es. Vuole vendere entro fine anno, immobile ristrutturato..." />
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 mb-2">Prima di inviare, conferma i consensi obbligatori.</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs">
                    <a href={`${API}/documents/privacy.pdf`} target="_blank" rel="noopener noreferrer" className="text-[#8a6d0f] font-medium hover:underline" data-testid="consent-download-privacy">↓ Scarica Informativa Privacy</a>
                    <a href={`${API}/documents/programma-segnalatori.pdf`} target="_blank" rel="noopener noreferrer" className="text-[#8a6d0f] font-medium hover:underline" data-testid="consent-download-programma">↓ Scarica Contratto Segnalatore (Termini e Condizioni)</a>
                  </div>
                  <Consent checked={consents.privacy} onChange={(v) => setConsents({ ...consents, privacy: v })} testid="c-privacy"
                    label="Ho preso visione della Privacy Policy." required />
                  <Consent checked={consents.program_terms} onChange={(v) => setConsents({ ...consents, program_terms: v })} testid="c-terms"
                    label="Accetto i Termini del Programma Segnalatori." required />
                  <Consent checked={consents.data_legitimacy} onChange={(v) => setConsents({ ...consents, data_legitimacy: v })} testid="c-legit"
                    label="Dichiaro che la comunicazione dei dati del contatto è legittima." required />
                  <Consent checked={consents.marketing} onChange={(v) => setConsents({ ...consents, marketing: v })} testid="c-marketing"
                    label="Acconsento a ricevere comunicazioni (facoltativo)." />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 disabled:opacity-0">
              <ArrowLeft className="h-4 w-4" /> Indietro
            </button>
            {step < 3 ? (
              <button onClick={next} data-testid="wizard-next-btn" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2">
                Continua <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={() => submit(false)} disabled={loading || !steps[3].valid()} data-testid="wizard-submit-btn"
                className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Invia segnalazione
              </button>
            )}
          </div>
        </div>
      </div>

      {dupWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5" data-testid="duplicate-modal">
          <div className="max-w-md rounded-2xl bg-white p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">Possibile segnalazione già presente</h3>
            <p className="mt-2 text-sm text-slate-500">
              Abbiamo trovato una pratica potenzialmente corrispondente. Sarà richiesta una verifica
              amministrativa prima dell'attribuzione. Vuoi comunque inviarla per la verifica?
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setDupWarn(false)} className="flex-1 rounded-full border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700">Annulla</button>
              <button onClick={() => { setDupWarn(false); submit(true); }} data-testid="dup-confirm-btn"
                className="btn-gold flex-1 rounded-full px-4 py-3 text-sm font-semibold">Invia per verifica</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
    </div>
  );
}

function Consent({ label, checked, onChange, required, testid }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-slate-300">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid}
        className="mt-0.5 h-5 w-5 accent-[#D4AF37]" />
      <span className="text-sm text-slate-700">{label}{required && <span className="text-rose-500"> *</span>}</span>
    </label>
  );
}
